// frontend/src/pages/FleetFuelPage.js
import React, { useState, useEffect, useCallback } from 'react';
import useApi from '../hooks/useApi';
import { Plus, Search, Edit, Trash2, X, Fuel, Calendar, MapPin, Users, Activity, ChevronLeft, ChevronRight, Hash } from 'lucide-react';

const formatDateUTC = (dateString) => {
    if (!dateString) return '-';
    const dateOnly = dateString.split('T')[0]; 
    return new Date(dateOnly).toLocaleDateString('pt-BR', { timeZone: 'UTC' });
};

const FleetFuelPage = ({ currentUser, selectedCurrency, formatCurrency }) => {
    const { apiFetch } = useApi();
    const [records, setRecords] = useState([]);
    const [loading, setLoading] = useState(false);
    const [hasSearched, setHasSearched] = useState(false); 
    
    // Lista de usuários para o filtro do Admin
    const [usersList, setUsersList] = useState([]);
    // Lista de equipes cadastradas
    const [teamsList, setTeamsList] = useState([]);

    // Paginação e Totais
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalGasto, setTotalGasto] = useState(0);

    // Permissões
    const canView = currentUser?.is_super_user || currentUser?.permissions?.fleet_fuel_view;
    const canCreate = currentUser?.is_super_user || currentUser?.permissions?.fleet_fuel_create;
    const canEdit = currentUser?.is_super_user || currentUser?.permissions?.fleet_fuel_edit;
    const canDelete = currentUser?.is_super_user || currentUser?.permissions?.fleet_fuel_delete;
    const isSuperOrAdmin = currentUser?.is_super_user || currentUser?.permissions?.manageUsers;

    // Filtros
    const [filterMonth, setFilterMonth] = useState(new Date().toISOString().slice(0, 7));
    const [filterStartDate, setFilterStartDate] = useState('');
    const [filterEndDate, setFilterEndDate] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [filterUserId, setFilterUserId] = useState('todos');

    // Modal
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingRecord, setEditingRecord] = useState(null);

    // Formulário
    const [date, setDate] = useState('');
    const [stationName, setStationName] = useState('');
    const [amount, setAmount] = useState('');
    const [vehicleKm, setVehicleKm] = useState('');
    const [team, setTeam] = useState('');
    const [licensePlate, setLicensePlate] = useState(''); 
    const [formError, setFormError] = useState('');
    const [formLoading, setFormLoading] = useState(false);

    // Busca a lista de usuários caso seja Admin
    useEffect(() => {
        if (isSuperOrAdmin) {
            apiFetch('/users').then(res => {
                if (Array.isArray(res)) setUsersList(res);
            }).catch(console.error);
        }
    }, [isSuperOrAdmin, apiFetch]);

    // Busca a lista de Equipes para popular o Select no formulário
    // A rota backend agora cuida da permissão e retorna apenas a equipe correta para o usuário comum
    useEffect(() => {
        apiFetch('/fleet/teams').then(res => {
            if (Array.isArray(res)) setTeamsList(res);
        }).catch(console.error);
    }, [apiFetch]);

    // AUTO-PREENCHIMENTO DE EQUIPE:
    // Se o usuário só tiver 1 equipe liberada, já seleciona automaticamente
    useEffect(() => {
        if (isModalOpen && !editingRecord && teamsList.length === 1 && !team) {
            setTeam(teamsList[0].name);
        }
    }, [teamsList, isModalOpen, editingRecord, team]);

    const fetchRecords = useCallback(async (page = 1) => {
        if (!canView) return;
        setLoading(true);
        setHasSearched(true);
        try {
            const params = new URLSearchParams();
            if (filterMonth) params.append('month', filterMonth);
            if (filterStartDate) params.append('startDate', filterStartDate);
            if (filterEndDate) params.append('endDate', filterEndDate);
            if (searchQuery) params.append('search', searchQuery);
            if (isSuperOrAdmin && filterUserId !== 'todos') params.append('userId', filterUserId);
            params.append('page', page);

            const response = await apiFetch(`/fleet/fuel?${params.toString()}`);
            
            setRecords(Array.isArray(response.data) ? response.data : []);
            setTotalPages(response.totalPages || 1);
            setCurrentPage(response.currentPage || 1);
            setTotalGasto(response.totalGasto || 0);
            
        } catch (error) {
            console.error("Erro ao buscar abastecimentos:", error);
            alert("Falha ao carregar registros.");
        } finally {
            setLoading(false);
        }
    }, [apiFetch, canView, filterMonth, filterStartDate, filterEndDate, searchQuery, filterUserId, isSuperOrAdmin]);

    const handleSearchClick = () => {
        fetchRecords(1);
    };

    const openModal = (record = null) => {
        setFormError('');
        if (record) {
            setEditingRecord(record);
            setDate(record.date.split('T')[0]);
            setStationName(record.station_name);
            setAmount(record.amount);
            setVehicleKm(record.vehicle_km);
            setTeam(record.team);
            setLicensePlate(record.license_plate || '');
        } else {
            setEditingRecord(null);
            setDate(new Date().toISOString().split('T')[0]);
            setStationName('');
            setAmount('');
            setVehicleKm('');
            setLicensePlate('');
            
            // Força a auto-seleção imediata caso as equipes já estejam carregadas
            if (teamsList.length === 1) {
                setTeam(teamsList[0].name);
            } else {
                setTeam('');
            }
        }
        setIsModalOpen(true);
    };

    const closeModal = () => {
        setIsModalOpen(false);
        setEditingRecord(null);
    };

    const handleSave = async (e) => {
        e.preventDefault();
        setFormError('');
        setFormLoading(true);

        if (!date || !stationName || !amount || !vehicleKm || !team || !licensePlate) {
            setFormError("Preencha todos os campos obrigatórios (incluindo a placa e equipe).");
            setFormLoading(false);
            return;
        }

        const payload = {
            date,
            station_name: stationName,
            amount: parseFloat(amount),
            vehicle_km: parseInt(vehicleKm, 10),
            team,
            license_plate: licensePlate.toUpperCase()
        };

        try {
            if (editingRecord) {
                await apiFetch(`/fleet/fuel/${editingRecord.id}`, {
                    method: 'PUT',
                    body: payload
                });
            } else {
                await apiFetch('/fleet/fuel', {
                    method: 'POST',
                    body: payload
                });
            }
            closeModal();
            // Se já tiver pesquisado, atualiza a lista
            if (hasSearched) {
                fetchRecords(currentPage);
            }
        } catch (error) {
            setFormError(`Erro ao salvar: ${error.message}`);
        } finally {
            setFormLoading(false);
        }
    };

    const handleDelete = async (id) => {
        if (window.confirm("Tem a certeza que deseja excluir este registro de abastecimento?")) {
            try {
                await apiFetch(`/fleet/fuel/${id}`, { method: 'DELETE' });
                if (hasSearched) fetchRecords(currentPage);
            } catch (error) {
                alert(`Erro ao excluir: ${error.message}`);
            }
        }
    };

    const handleStartDateChange = (e) => {
        setFilterStartDate(e.target.value);
        if (e.target.value) setFilterMonth('');
    };

    const handleEndDateChange = (e) => {
        setFilterEndDate(e.target.value);
        if (e.target.value) setFilterMonth('');
    };

    const handleMonthChange = (e) => {
        setFilterMonth(e.target.value);
        if (e.target.value) {
            setFilterStartDate('');
            setFilterEndDate('');
        }
    };

    if (!canView) {
        return <div className="p-8 text-center text-gray-500 font-bold">Você não tem permissão para acessar este módulo.</div>;
    }

    return (
        <div className="space-y-6 animate-in fade-in zoom-in-95 duration-300">
            {/* Cabeçalho */}
            <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
                <div>
                    <h1 className="text-2xl font-black text-gray-800 flex items-center gap-2">
                        <Fuel className="text-blue-600" size={28} />
                        Controle de Combustível
                    </h1>
                    <p className="text-gray-500 text-sm mt-1">Gestão de abastecimentos e quilometragem da frota.</p>
                </div>
                {canCreate && (
                    <button 
                        onClick={() => openModal()} 
                        className="bg-blue-600 text-white px-4 py-2.5 rounded-lg font-bold shadow-sm hover:bg-blue-700 transition-colors flex items-center gap-2 w-full md:w-auto justify-center"
                    >
                        <Plus size={18} /> Novo Abastecimento
                    </button>
                )}
            </div>

            {/* Filtros */}
            <div className="bg-white p-4 rounded-xl shadow-sm border border-gray-200">
                <div className="grid grid-cols-1 md:grid-cols-12 gap-4 items-end">
                    
                    {/* Filtro de Usuário (Apenas Admin) */}
                    {isSuperOrAdmin && (
                        <div className="md:col-span-3">
                            <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Funcionário</label>
                            <select 
                                value={filterUserId} 
                                onChange={(e) => setFilterUserId(e.target.value)}
                                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            >
                                <option value="todos">Todos os Funcionários</option>
                                {usersList.map(u => (
                                    <option key={u.id} value={u.id}>{u.username}</option>
                                ))}
                            </select>
                        </div>
                    )}

                    <div className={`${isSuperOrAdmin ? 'md:col-span-3' : 'md:col-span-4'} relative`}>
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Pesquisar</label>
                        <div className="relative">
                            <Search className="absolute left-3 top-2.5 text-gray-400" size={16} />
                            <input 
                                type="text" 
                                placeholder="Posto, Equipe, Placa ou KM..." 
                                value={searchQuery}
                                onChange={(e) => setSearchQuery(e.target.value)}
                                className="w-full pl-9 pr-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                            />
                        </div>
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Mês Ref.</label>
                        <input 
                            type="month" 
                            value={filterMonth}
                            onChange={handleMonthChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Início</label>
                        <input 
                            type="date" 
                            value={filterStartDate}
                            onChange={handleStartDateChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    
                    <div className="md:col-span-2">
                        <label className="text-xs font-bold text-gray-500 uppercase block mb-1">Data Fim</label>
                        <input 
                            type="date" 
                            value={filterEndDate}
                            onChange={handleEndDateChange}
                            className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-1 focus:ring-blue-500 outline-none"
                        />
                    </div>
                    
                    <div className="md:col-span-12 mt-2">
                        <button 
                            onClick={handleSearchClick} 
                            className="w-full bg-gray-800 text-white px-4 py-2.5 rounded-lg font-bold shadow-sm hover:bg-gray-900 transition-colors flex items-center justify-center gap-2"
                        >
                            <Search size={18} /> Filtrar Resultados
                        </button>
                    </div>
                </div>
            </div>

            {/* Tabela de Dados e Visão Mobile */}
            {hasSearched && (
                <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden animate-in slide-in-from-bottom-4">
                    
                    {/* --- VISÃO DESKTOP (TABELA) --- */}
                    <div className="hidden md:block overflow-x-auto">
                        <table className="w-full text-sm text-left">
                            <thead className="bg-gray-50 text-gray-700 border-b border-gray-200">
                                <tr>
                                    <th className="p-4 font-bold uppercase text-xs tracking-wider">Data</th>
                                    <th className="p-4 font-bold uppercase text-xs tracking-wider">Posto de Combustível</th>
                                    <th className="p-4 font-bold uppercase text-xs tracking-wider">Equipe & Placa</th>
                                    <th className="p-4 font-bold uppercase text-xs tracking-wider">KM Registrado</th>
                                    <th className="p-4 font-bold uppercase text-xs tracking-wider text-right">Valor Gasto</th>
                                    {(canEdit || canDelete) && <th className="p-4 font-bold uppercase text-xs tracking-wider text-center w-24">Ações</th>}
                                </tr>
                            </thead>
                            <tbody className="divide-y divide-gray-100">
                                {loading ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500 font-medium">Carregando registros...</td>
                                    </tr>
                                ) : records.length === 0 ? (
                                    <tr>
                                        <td colSpan="6" className="p-8 text-center text-gray-500 font-medium italic bg-gray-50/50">Nenhum abastecimento encontrado para os filtros selecionados.</td>
                                    </tr>
                                ) : (
                                    records.map(record => {
                                        // Verifica se o usuário atual é o dono do registro (ou admin) para liberar edição/exclusão
                                        const isOwner = record.user_id === currentUser.id;
                                        const canModifyThis = isSuperOrAdmin || isOwner;

                                        return (
                                            <tr key={record.id} className="hover:bg-blue-50/30 transition-colors">
                                                <td className="p-4 text-gray-600 font-medium flex items-center gap-2">
                                                    <Calendar size={14} className="text-gray-400"/>
                                                    {formatDateUTC(record.date)}
                                                </td>
                                                <td className="p-4 text-gray-800 font-bold">
                                                    <div className="flex items-center gap-2">
                                                        <MapPin size={14} className="text-red-500"/>
                                                        {record.station_name}
                                                    </div>
                                                </td>
                                                <td className="p-4 text-gray-700">
                                                    <div className="flex flex-col gap-1">
                                                        <div className="flex items-center gap-2">
                                                            <Users size={14} className="text-blue-500"/>
                                                            {record.team}
                                                        </div>
                                                        <div className="flex items-center gap-2 text-xs text-gray-500 font-medium ml-1">
                                                            <Hash size={12} className="text-gray-400"/>
                                                            Placa: {record.license_plate || 'N/I'}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="p-4 text-gray-700 font-mono">
                                                    <div className="flex items-center gap-2">
                                                        <Activity size={14} className="text-green-600"/>
                                                        {record.vehicle_km.toLocaleString('pt-BR')} km
                                                    </div>
                                                </td>
                                                <td className="p-4 text-right font-black text-red-600">
                                                    {formatCurrency(record.amount, selectedCurrency)}
                                                </td>
                                                {(canEdit || canDelete) && (
                                                    <td className="p-4 text-center">
                                                        <div className="flex items-center justify-center gap-2">
                                                            {canEdit && canModifyThis && (
                                                                <button onClick={() => openModal(record)} className="p-1.5 text-blue-600 hover:bg-blue-100 rounded transition-colors" title="Editar">
                                                                    <Edit size={16} />
                                                                </button>
                                                            )}
                                                            {canDelete && canModifyThis && (
                                                                <button onClick={() => handleDelete(record.id)} className="p-1.5 text-red-600 hover:bg-red-100 rounded transition-colors" title="Excluir">
                                                                    <Trash2 size={16} />
                                                                </button>
                                                            )}
                                                            {(!canModifyThis) && <span className="text-xs text-gray-400 italic">Restrito</span>}
                                                        </div>
                                                    </td>
                                                )}
                                            </tr>
                                        )
                                    })
                                )}
                            </tbody>
                            {!loading && records.length > 0 && (
                                <tfoot className="bg-gray-100/80 border-t-2 border-gray-300">
                                    <tr>
                                        <td colSpan={(canEdit || canDelete) ? "4" : "4"} className="p-4 text-right font-bold text-gray-700 uppercase tracking-wider">
                                            Total Gasto no Período:
                                        </td>
                                        <td className="p-4 text-right font-black text-lg text-red-700">
                                            {formatCurrency(totalGasto, selectedCurrency)}
                                        </td>
                                        {(canEdit || canDelete) && <td></td>}
                                    </tr>
                                </tfoot>
                            )}
                        </table>
                    </div>

                    {/* --- VISÃO MOBILE (CARDS) --- */}
                    <div className="block md:hidden">
                        {loading ? (
                            <div className="p-8 text-center text-gray-500 font-medium">Carregando registros...</div>
                        ) : records.length === 0 ? (
                            <div className="p-8 text-center text-gray-500 font-medium italic bg-gray-50/50">Nenhum abastecimento encontrado.</div>
                        ) : (
                            <div className="divide-y divide-gray-100">
                                {records.map(record => {
                                    const isOwner = record.user_id === currentUser.id;
                                    const canModifyThis = isSuperOrAdmin || isOwner;
                                    
                                    return (
                                        <div key={record.id} className="p-4 space-y-3 hover:bg-blue-50/30 transition-colors">
                                            <div className="flex justify-between items-start">
                                                <div className="flex items-center gap-2 text-gray-600 font-medium text-sm">
                                                    <Calendar size={14} className="text-gray-400"/>
                                                    {formatDateUTC(record.date)}
                                                </div>
                                                <div className="font-black text-red-600">
                                                    {formatCurrency(record.amount, selectedCurrency)}
                                                </div>
                                            </div>
                                            
                                            <div className="space-y-2">
                                                <div className="flex items-center gap-2 text-gray-800 font-bold text-sm">
                                                    <MapPin size={14} className="text-red-500"/>
                                                    {record.station_name}
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-700 text-sm">
                                                    <Users size={14} className="text-blue-500"/>
                                                    {record.team}
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-500 text-sm font-medium">
                                                    <Hash size={14} className="text-gray-400"/>
                                                    Placa: {record.license_plate || 'N/I'}
                                                </div>
                                                <div className="flex items-center gap-2 text-gray-700 font-mono text-sm">
                                                    <Activity size={14} className="text-green-600"/>
                                                    {record.vehicle_km.toLocaleString('pt-BR')} km
                                                </div>
                                            </div>
                                            
                                            {(canEdit || canDelete) && (
                                                <div className="pt-3 mt-3 border-t border-gray-100 flex justify-end gap-2">
                                                    {canEdit && canModifyThis && (
                                                        <button onClick={() => openModal(record)} className="p-2 text-blue-600 bg-blue-50 hover:bg-blue-100 rounded transition-colors" title="Editar">
                                                            <Edit size={16} />
                                                        </button>
                                                    )}
                                                    {canDelete && canModifyThis && (
                                                        <button onClick={() => handleDelete(record.id)} className="p-2 text-red-600 bg-red-50 hover:bg-red-100 rounded transition-colors" title="Excluir">
                                                            <Trash2 size={16} />
                                                        </button>
                                                    )}
                                                    {(!canModifyThis) && <span className="text-xs text-gray-400 italic py-2">Restrito</span>}
                                                </div>
                                            )}
                                        </div>
                                    );
                                })}
                                {!loading && records.length > 0 && (
                                    <div className="p-4 bg-gray-100/80 border-t-2 border-gray-300 flex justify-between items-center">
                                        <span className="font-bold text-gray-700 uppercase tracking-wider text-xs">Total Gasto:</span>
                                        <span className="font-black text-lg text-red-700">{formatCurrency(totalGasto, selectedCurrency)}</span>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    {/* Paginação */}
                    {!loading && totalPages > 1 && (
                        <div className="bg-white px-4 py-3 flex items-center justify-between border-t border-gray-200 sm:px-6">
                            <div className="flex-1 flex justify-between sm:hidden">
                                <button onClick={() => fetchRecords(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                                    Anterior
                                </button>
                                <span className="text-sm text-gray-700 py-2">Pág {currentPage} de {totalPages}</span>
                                <button onClick={() => fetchRecords(currentPage + 1)} disabled={currentPage === totalPages} className="ml-3 relative inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50">
                                    Próximo
                                </button>
                            </div>
                            <div className="hidden sm:flex-1 sm:flex sm:items-center sm:justify-between">
                                <div>
                                    <p className="text-sm text-gray-700">
                                        Página <span className="font-medium">{currentPage}</span> de <span className="font-medium">{totalPages}</span>
                                    </p>
                                </div>
                                <div>
                                    <nav className="relative z-0 inline-flex rounded-md shadow-sm -space-x-px" aria-label="Pagination">
                                        <button onClick={() => fetchRecords(currentPage - 1)} disabled={currentPage === 1} className="relative inline-flex items-center px-2 py-2 rounded-l-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                                            <span className="sr-only">Anterior</span>
                                            <ChevronLeft className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                        
                                        {[...Array(totalPages)].map((_, i) => (
                                            <button
                                                key={i + 1}
                                                onClick={() => fetchRecords(i + 1)}
                                                className={`relative inline-flex items-center px-4 py-2 border text-sm font-medium ${currentPage === i + 1 ? 'z-10 bg-blue-50 border-blue-500 text-blue-600' : 'bg-white border-gray-300 text-gray-500 hover:bg-gray-50'}`}
                                            >
                                                {i + 1}
                                            </button>
                                        ))}

                                        <button onClick={() => fetchRecords(currentPage + 1)} disabled={currentPage === totalPages} className="relative inline-flex items-center px-2 py-2 rounded-r-md border border-gray-300 bg-white text-sm font-medium text-gray-500 hover:bg-gray-50 disabled:opacity-50">
                                            <span className="sr-only">Próximo</span>
                                            <ChevronRight className="h-5 w-5" aria-hidden="true" />
                                        </button>
                                    </nav>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* Modal de Formulário */}
            {isModalOpen && (
                <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-in fade-in duration-200">
                    <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg overflow-hidden animate-in slide-in-from-bottom-4 duration-300">
                        <div className="flex items-center justify-between p-5 border-b border-gray-200 bg-gray-50">
                            <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                                <Fuel className="text-blue-600" />
                                {editingRecord ? 'Editar Abastecimento' : 'Novo Abastecimento'}
                            </h2>
                            <button onClick={closeModal} className="p-2 rounded-full hover:bg-gray-200 text-gray-500 transition-colors">
                                <X size={20} />
                            </button>
                        </div>
                        
                        <form onSubmit={handleSave} className="p-6 space-y-4">
                            {formError && (
                                <div className="p-3 text-sm font-semibold text-red-700 bg-red-100 border border-red-200 rounded-lg">
                                    {formError}
                                </div>
                            )}

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Data</label>
                                    <input 
                                        type="date" 
                                        value={date} 
                                        onChange={(e) => setDate(e.target.value)} 
                                        required 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Equipe</label>
                                    <select 
                                        value={team} 
                                        onChange={(e) => setTeam(e.target.value)} 
                                        required 
                                        disabled={!isSuperOrAdmin && teamsList.length === 1}
                                        className={`w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none ${(!isSuperOrAdmin && teamsList.length === 1) ? 'bg-gray-100 text-gray-700 font-bold cursor-not-allowed' : 'bg-white'}`}
                                    >
                                        {teamsList.length !== 1 && <option value="" disabled>Selecione uma Equipe...</option>}
                                        {teamsList.map(t => (
                                            <option key={t.id} value={t.name}>{t.name}</option>
                                        ))}
                                    </select>
                                    {teamsList.length === 0 && (
                                        <p className="text-[10px] text-orange-600 mt-1 font-bold">Nenhuma equipe vinculada.</p>
                                    )}
                                </div>
                                
                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Placa do Veículo</label>
                                    <input 
                                        type="text" 
                                        value={licensePlate} 
                                        onChange={(e) => setLicensePlate(e.target.value)} 
                                        placeholder="Ex: ABC-1234"
                                        required 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none font-mono uppercase"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Nome do Posto</label>
                                    <input 
                                        type="text" 
                                        value={stationName} 
                                        onChange={(e) => setStationName(e.target.value)} 
                                        placeholder="Ex: Posto Ipiranga Centro"
                                        required 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">KM do Veículo</label>
                                    <input 
                                        type="number" 
                                        value={vehicleKm} 
                                        onChange={(e) => setVehicleKm(e.target.value)} 
                                        placeholder="Ex: 145000"
                                        min="0"
                                        required 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none"
                                    />
                                </div>

                                <div>
                                    <label className="block text-sm font-bold text-gray-700 mb-1">Valor do Combustível ({selectedCurrency})</label>
                                    <input 
                                        type="number" 
                                        step="0.01"
                                        min="0"
                                        value={amount} 
                                        onChange={(e) => setAmount(e.target.value)} 
                                        placeholder="0.00"
                                        required 
                                        className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 outline-none text-red-600 font-bold"
                                    />
                                </div>
                            </div>

                            <div className="flex justify-end gap-3 pt-4 border-t border-gray-100 mt-6">
                                <button 
                                    type="button" 
                                    onClick={closeModal} 
                                    className="px-5 py-2.5 font-bold text-gray-600 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
                                >
                                    Cancelar
                                </button>
                                <button 
                                    type="submit" 
                                    disabled={formLoading || (!isSuperOrAdmin && teamsList.length === 0)} 
                                    className="px-5 py-2.5 font-bold text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:bg-blue-400 transition-colors flex items-center gap-2"
                                >
                                    {formLoading ? 'Salvando...' : 'Salvar Registro'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default FleetFuelPage;